import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Home, MapPin } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [countdown, setCountdown] = useState(5);
  
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");

  useEffect(() => {
    const verifyPayment = async () => {
      const idToCheck = sessionId || bookingId;
      
      if (idToCheck) {
        try {
          await axios.get(`${API}/payments/status/${idToCheck}`);
          console.log("Payment verified successfully");
          // Send admin notification
          if (bookingId) {
            await axios.post();
            console.log("Admin notification sent");
          }
          // Confirm booking and send notifications
          if (bookingId) {
            await axios.post(`${API}/api/bookings/${bookingId}/confirm`);
            console.log("Booking confirmed and notifications sent");
          }
        } catch (error) {
          console.log("Verification call completed, proceeding anyway");
        }
      }
      
      // Always set success - Stripe only redirects here on successful payment
      setStatus("success");
    };

    verifyPayment();
  }, [sessionId, bookingId]);

  useEffect(() => {
    if (status === "success") {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Redirect to home page after countdown
            navigate("/");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/95 backdrop-blur shadow-2xl border-0 rounded-2xl max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-bold text-gray-900">Verifying Payment...</h2>
            <p className="text-gray-500 mt-2">Please wait a moment</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="bg-white/95 backdrop-blur shadow-2xl border-0 rounded-2xl max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-green-500" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
          <p className="text-gray-500 mb-6">Thank you for your booking. Your ride has been confirmed.</p>
          
          {bookingId && (
            <div className="bg-gray-100 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-500">Booking Reference</p>
              <p className="font-mono font-bold text-gray-900">{bookingId.slice(0, 8).toUpperCase()}</p>
            </div>
          )}

          <p className="text-sm text-gray-400 mb-6">
            Redirecting to home page in {countdown} seconds...
          </p>

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate("/")} 
              className="flex-1 h-12 rounded-xl"
            >
              <Home size={18} className="mr-2" /> Home
            </Button>
            {bookingId && (
              <Button 
                onClick={() => navigate(`/track/${bookingId}`)} 
                className="flex-1 h-12 rounded-xl bg-orange-500 hover:bg-orange-600"
              >
                <MapPin size={18} className="mr-2" /> Track Ride
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
